import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import '../styles/Leaderboards.css';

function Leaderboards() {
  const { token, user, logout } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const [leagues, setLeagues] = useState([]);
  const [standingsMap, setStandingsMap] = useState({});
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/leagues`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) return;

      setLeagues(data.leagues);

      const sMap = {};
      await Promise.all(
        data.leagues.map(async (league) => {
          try {
            const sRes = await fetch(`${API_URL}/scores/${league._id}/standings`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const sData = await sRes.json();
            if (sData.success) {
              sMap[league._id] = sData.standings;
            }
          } catch {
            // ignore
          }
        })
      );

      setStandingsMap(sMap);
    } catch {
      // leagues will just be empty
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="leaderboards-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <Link to="/dashboard" className="btn btn-secondary">Dashboard</Link>
          <Link to="/leagues" className="btn btn-secondary">Leagues</Link>
          <button onClick={logout} className="btn btn-logout">Logout</button>
        </div>
      </nav>

      <div className="leaderboards-content">
        <header className="leaderboards-header">
          <h2>🏆 Leaderboards</h2>
          <p>Standings for all your leagues</p>
        </header>

        {loading && <p className="status-msg">Loading leaderboards...</p>}

        {!loading && leagues.length === 0 && (
          <div className="empty-state">
            <p>Join a league to see leaderboards!</p>
            <Link to="/leagues" className="btn btn-primary">Browse Leagues</Link>
          </div>
        )}

        {!loading && leagues.map(league => {
          const standings = standingsMap[league._id] || [];

          return (
            <div key={league._id} className="leaderboard-card">
              <div className="leaderboard-card-header">
                <div>
                  <Link to={`/leagues/${league._id}`} className="leaderboard-league-name">
                    {league.name}
                  </Link>
                  <span className="leaderboard-tournament">{league.tournament?.name}</span>
                </div>
              </div>

              {standings.length > 0 ? (
                <table className="leaderboard-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Manager</th>
                      <th>Players</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((entry, idx) => (
                      <tr
                        key={entry.manager._id}
                        className={entry.manager._id === user?._id ? 'leaderboard-you' : ''}
                      >
                        <td className="lb-rank">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : idx + 1}
                        </td>
                        <td>
                          {entry.manager.firstName || entry.manager.username}
                          {entry.manager._id === user?._id && <span className="you-badge">You</span>}
                        </td>
                        <td>{entry.players.length}</td>
                        <td className="lb-points">{entry.totalPoints}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="leaderboard-empty">
                  <p>No scores yet — tournament hasn't started or no results entered.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Leaderboards;
